import { HttpErrorResponse } from '@angular/common/http';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, Input, OnDestroy, Output } from '@angular/core';
import { Observable, Subscription } from 'rxjs';
import { map } from 'rxjs/operators';
import { DsButtonVariants, Icons } from '@levelaccess/design-system';

import { AuditTypes } from '@shared/constants/audit-types';
import { RuleAuditHistoryAction } from '@shared/constants/rule-audit-history';
import { $scanIssues } from '@shared/constants/scan-issues';
import { IAutomatedScanIssue, IScanIssues } from '@shared/interfaces/scan-issues.interface';
import { ITask } from '@shared/interfaces/task.interface';
import { NotificationPosition } from '@client/app/models/notification.model';
import { LoadErrorHandler } from '@client/app/services/load-error-handler';
import { NotificationService } from '@client/app/services/notification.service';
import { ScanReportService } from '@client/app/services/scan-report.service';
import { TranslateService } from '@client/app/translate/translate.service';
import { AclSecurityAdapter } from '@client/app/services/acl.service';
import { FunctionalArea, SecureAction } from '@shared/constants/security';
import { IRequiredSecurity } from '@shared/interfaces/security.interface';
import { RequiredSecurities } from '@shared/constants/required-securities';
import { UserAclService } from '@client/app/services/user-acl.service';
import { IIssueActionContext, IssueActionsService } from '@client/app/services/issue-finding-details/issue-actions.service';
import { BannerStatus } from '../issues-view-banner/issues-view-banner.component';

@Component({
  selector: 'app-issues-view-table-actions',
  templateUrl: './issues-view-table-actions.component.html',
  styleUrls: ['./issues-view-table-actions.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class IssuesViewTableActionsComponent implements OnDestroy {
  private subscriptions: Subscription;
  private _selectedIssues: IAutomatedScanIssue[];
  @Input()
  public scanIssues: IScanIssues;
  @Input()
  public scanId: string;
  @Input()
  public scanUrl: string;
  @Input()
  public auditType: AuditTypes;
  @Input()
  public ruleId: string;
  @Input()
  public filterByActivity: boolean | null;
  @Output()
  public filterByActivityChange: EventEmitter<boolean | null>;
  @Output()
  public onIssueChangeSuccess: EventEmitter<ITask>;
  @Output()
  public bannerStatusEvent: EventEmitter<BannerStatus>;
  public $scanIssues: typeof $scanIssues;
  public readonly DsButtonVariants: typeof DsButtonVariants = DsButtonVariants;
  public readonly Icons: typeof Icons = Icons;

  public downloadScanRequiredSecurity: IRequiredSecurity;

  @Input()
  public set selectedIssues(issues: IAutomatedScanIssue[]) {
    this._selectedIssues = issues;
    this.changeDetectorRef.detectChanges();
  }

  public get selectedIssues(): IAutomatedScanIssue[] {
    return this._selectedIssues;
  }

  public hasCreateTaskRequiredSecurity$: Observable<boolean>;

  public hasDismissFindingRequiredSecurity$: Observable<boolean>;

  constructor(
    private loadErrorHandler: LoadErrorHandler,
    private notificationService: NotificationService,
    private translateService: TranslateService,
    private scanReportService: ScanReportService,
    private changeDetectorRef: ChangeDetectorRef,
    private userAclService: UserAclService,
    private issueActionsService: IssueActionsService,
  ) {
    this.$scanIssues = $scanIssues;
    this.onIssueChangeSuccess = new EventEmitter();
    this.filterByActivityChange = new EventEmitter();
    this.bannerStatusEvent = new EventEmitter();
    this.subscriptions = new Subscription();
    this.downloadScanRequiredSecurity = RequiredSecurities.AT_Scans_Download;

    this.hasCreateTaskRequiredSecurity$ = this.userAclService.createCheckAccessForCurrentUser().pipe(
      map((adapter: AclSecurityAdapter): boolean =>
        adapter
          .useWorkspaceFromUser()
          .useFunctionalActions({
            [FunctionalArea.pm_tasks]: [SecureAction.create],
          })
          .check(),
      ),
    );

    this.hasDismissFindingRequiredSecurity$ = this.userAclService
      .createCheckAccessForCurrentUser()
      .pipe(
        map((adapter: AclSecurityAdapter): boolean =>
          adapter
            .useWorkspaceFromUser()
            .useDigitalPropertyFromUser()
            .useFunctionalActions(RequiredSecurities.AT_Scans_Update.functionalActions)
            .check(),
        ),
      );
  }

  private onRestoreIssuesSuccess(updatedCount: number): void {
    if (updatedCount < this._selectedIssues.length) {
      this.notificationService.warn(
        this.translateService.instant(
          this._selectedIssues.length - updatedCount === 1
            ? 'unmark_error_as_false_positive_warning'
            : 'unmark_errors_as_false_positive_warning',
        ),
        NotificationPosition.Toast,
      );
    } else {
      this.notificationService.success(
        this.translateService.instant(
          updatedCount === 1 ? 'unmark_error_as_false_positive_success' : 'unmark_errors_as_false_positive_success',
        ),
        NotificationPosition.Toast,
      );
    }

    this.onIssueChangeSuccess.emit();
    this.bannerStatusEvent.emit(BannerStatus.findingRestore);
  }

  private onDismissIssuesSuccess(updatedCount: number): void {
    if (updatedCount < this._selectedIssues.length) {
      this.notificationService.warn(
        this.translateService.instant(
          this._selectedIssues.length - updatedCount === 1
            ? 'mark_error_as_false_positive_warning'
            : 'mark_errors_as_false_positive_warning',
        ),
        NotificationPosition.Toast,
      );
    } else {
      this.notificationService.success(
        this.translateService.instant(
          updatedCount === 1 ? 'mark_error_as_false_positive_success' : 'mark_errors_as_false_positive_success',
        ),
        NotificationPosition.Toast,
      );
    }

    this.onIssueChangeSuccess.emit();
    this.bannerStatusEvent.emit(BannerStatus.findingsDismissed);
  }

  public allDismissed(): boolean {
    if (this._selectedIssues.length === 0) {
      return false;
    }

    return this._selectedIssues.every((issue: IAutomatedScanIssue) => this.scanReportService.isDismissed(issue));
  }

  public allRestored(): boolean {
    if (this._selectedIssues.length === 0) {
      return false;
    }

    return this._selectedIssues.every((issue: IAutomatedScanIssue) => this.scanReportService.isDismissed(issue) === false);
  }

  public openBulkIssuesActionModal(isDismiss: boolean = false): void {
    if (this._selectedIssues.length === 0) {
      this.bannerStatusEvent.emit(BannerStatus.selectAtLeastOne);
      return;
    }

    if (this.containsClosedFindings()) {
      this.bannerStatusEvent.emit(BannerStatus.selectClosedFinding);
      return;
    }

    if (isDismiss && !this.allRestored()) {
      this.bannerStatusEvent.emit(BannerStatus.selectDismissedFinding);
      return;
    }

    const actionType: RuleAuditHistoryAction = this.allDismissed()
      ? RuleAuditHistoryAction.restore
      : RuleAuditHistoryAction.dismiss;

    const context: IIssueActionContext = {
      auditType: this.auditType,
      ruleDetails: this.scanIssues,
      scanId: this.scanId,
      scanUrl: this.scanUrl,
    };

    this.subscriptions.add(
      this.issueActionsService.openDismissOrRestoreModal(this._selectedIssues, actionType, context).subscribe({
        next: (updatedCount: number): void => {
          if (actionType === RuleAuditHistoryAction.dismiss) {
            this.onDismissIssuesSuccess(updatedCount);
          } else {
            this.onRestoreIssuesSuccess(updatedCount);
          }
        },
        error: (err: HttpErrorResponse): void => {
          if (actionType === RuleAuditHistoryAction.dismiss) {
            this.loadErrorHandler.handleError(
              this._selectedIssues.length === 1 ? 'mark_error_as_false_positive_error' : 'mark_errors_as_false_positive_error',
              err,
              IssuesViewTableActionsComponent.name,
            );
          } else {
            this.loadErrorHandler.handleError(
              this._selectedIssues.length === 1
                ? 'unmark_error_as_false_positive_error'
                : 'unmark_errors_as_false_positive_error',
              err,
              IssuesViewTableActionsComponent.name,
            );
          }
        },
      }),
    );
  }

  public canSelectedFindingsCreatingTask(): boolean {
    return this.selectedIssues.every((selectedIssue: IAutomatedScanIssue) =>
      this.scanReportService.canBeLinkedToTask(selectedIssue),
    );
  }

  public containsClosedFindings(): boolean {
    return this.selectedIssues.some((selectedIssue: IAutomatedScanIssue) => this.scanReportService.isIssueClosed(selectedIssue));
  }

  public openCreateTaskModal(): void {
    if (this._selectedIssues.length === 0) {
      this.bannerStatusEvent.emit(BannerStatus.selectAtLeastOne);
      return;
    }

    if (!this.allRestored()) {
      this.bannerStatusEvent.emit(BannerStatus.selectDismissedFinding);
      return;
    }

    if (!this.canSelectedFindingsCreatingTask()) {
      if (this.containsClosedFindings()) {
        this.bannerStatusEvent.emit(BannerStatus.selectLinkedClosedFinding);
        return;
      }
      this.bannerStatusEvent.emit(BannerStatus.selectLinkedFinding);
      return;
    }

    const context: IIssueActionContext = {
      auditType: this.auditType,
      ruleDetails: this.scanIssues,
      scanId: this.scanId,
      scanUrl: this.scanUrl,
    };

    this.subscriptions.add(
      this.issueActionsService.openCreateTaskSlideout(this._selectedIssues, context).subscribe({
        next: (task: ITask): void => {
          this.onIssueChangeSuccess.emit(task);
        },
      }),
    );
  }

  public ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }
}
